<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, type Ref } from 'vue'
import { api, type BacktestParams, type BacktestConfigStore, type OtpComponentAllocation, type ShadowStrategyParams } from '../api/client'

// ── OTP standalone knob surface (reduced, Gold/GGE precedent) ───────────
// v1 = run + read results only. No usePortfolio (hard-wired assetId
// 'otp'), no promote/publish affordance — promotion to live stays gated
// behind the funds' own backtest UIs, not this one. A minimal preset strip
// (load + save-as) was added for the Contrarian Fade best cell (2026-08-31)
// so it is selectable from this panel without hand-typing every knob; there
// is deliberately no "Apply to Live" / set-active button here, mirroring the
// v1 scope note above.
// Component param defaults below are copied verbatim from the Plan A
// evidence screen (tests/SideWinder.Backtest.Tests/Engine/
// OtpStandaloneScreen.cs BuildParams(), the live
// bga_futures_with_otp_experiment preset) so the panel's defaults
// reproduce the screened numbers exactly.

interface ComponentKnobs {
  enabled: boolean
  targetPct: number       // fraction of the day's residual, [0,1]
  triggerThreshold: number
  spreadBps: number
  annualTer: number
  leverageFactor: number
  dealingChargeGbp: number
  overnightGatePct: number | null
  fireWindowEndHourUk: number | null
}
interface ShortConvictionKnobs extends ComponentKnobs {
  bgaBeta: number
}

// Funding (spec 2026-08-30 §3.4). FixedNotional is the default: £50k is the
// idle carousel cash on a deep-drop day and the live LQQ3 ticket size, so P&L
// reads at realistic scale. Residual is the joint-portfolio view.
type OtpFundingMode = 'FixedNotional' | 'Residual'
const fundingMode = ref<OtpFundingMode>('FixedNotional')
const fixedNotionalGbp = ref(50000)
const portfolioNavGbp = ref(50000)
const cashReservePct = ref(0.10)
const stepAsideSlicePct = ref(0.25)

// Phase B (2026-09-06): LongOverlay disabled by default alongside LC/SC —
// all three legs are decommissioned. Historical presets that enabled it can
// still load their values into the panel (via loadPreset()) so past results
// remain inspectable, but the panel no longer initialises with LO active.
const overlay = ref<ComponentKnobs>({
  enabled: false,
  targetPct: 1.0,
  triggerThreshold: -2,
  spreadBps: 20,
  annualTer: 0.0075,
  leverageFactor: 3,
  dealingChargeGbp: 6.95,
  overnightGatePct: null,
  fireWindowEndHourUk: null,
})
const longConviction = ref<ComponentKnobs>({
  enabled: false,
  targetPct: 0,
  triggerThreshold: -1.7052,
  spreadBps: 5.5827,
  annualTer: 0.03177,
  leverageFactor: 3,
  dealingChargeGbp: 6.7535,
  overnightGatePct: null,
  fireWindowEndHourUk: null,
})
const shortConviction = ref<ShortConvictionKnobs>({
  enabled: false,
  targetPct: 0,
  triggerThreshold: 3.4887,
  spreadBps: 43.9225,
  annualTer: 0.02268,
  leverageFactor: -3,
  bgaBeta: 1.1245,
  dealingChargeGbp: 11.0803,
  overnightGatePct: null,
  fireWindowEndHourUk: null,
})

// Inert base fields: standalone mode derives its BUY/SELL day-type
// straight from the morning Nq0840OvernightRet print (BacktestEngine),
// bypassing the fund-dealing strategy entirely, so the usual BGA/JH knobs
// (z-window, IMR, VIX-mom gate, etc.) never influence a standalone run.
// Values mirror GgeBacktestPanel's inert defaults for consistency.
const baseParams = {
  zWindow: 90,
  tiltBps: 40,
  vixScale: 1.0,
  termStructure: false,
  nfpFilter: false,
  holidayFilter: true,
  opexFilter: true,
  quadWitchingFilter: true,
  exDividendFilter: true,
  futuresRollFilter: true,
  noHold: false,
  imrMode: 'filter',
  imrTiltBps: 0,
  imrWindow: 10,
  imrBreadthThreshold: 0.5,
  vixMomGate: 'off',
  autoOptimize: false,
  eomInterestFilter: true,
  eomInterestBoostBps: 15,
  useHedging: false,
} as const

const allocationSum = computed(() => {
  let sum = 0
  if (overlay.value.enabled) sum += overlay.value.targetPct
  if (longConviction.value.enabled) sum += longConviction.value.targetPct
  if (shortConviction.value.enabled) sum += shortConviction.value.targetPct
  return sum
})

const allocationError = computed(() => {
  if (allocationSum.value > 1) {
    return `Enabled allocations sum to ${(allocationSum.value * 100).toFixed(1)}% of residual — must be ≤ 100%.`
  }
  return null
})

// Phase B (2026-09-06): LC/LO/SC decommission. The panel still renders the
// three legs behind a read-only collapsible so historical presets remain
// inspectable, but any attempt to run a NEW backtest with one of them
// enabled is blocked here (and re-checked server-side at /api/backtest/run).
// See docs/superpowers/plans/2026-09-06-otp-shadow-promotion-arc.md.
const decommissionError = computed(() => {
  const enabled: string[] = []
  if (overlay.value.enabled) enabled.push('Long Overlay')
  if (longConviction.value.enabled) enabled.push('Long Conviction')
  if (shortConviction.value.enabled) enabled.push('Short Conviction')
  if (enabled.length === 0) return null
  return (
    `${enabled.join(' / ')} were decommissioned 2026-09-06. ` +
    'Use the shadow-strategy presets — see the Phase C.4 rewrite ' +
    'in docs/superpowers/plans/2026-09-06-otp-shadow-promotion-arc.md.'
  )
})

// v-model.number on an emptied input yields '' rather than null/undefined —
// normalise both to null so buildParams always sends a clean gate value.
const gate = (v: number | '' | null | undefined) => (v === '' || v == null ? null : v)

function buildParams(): BacktestParams {
  const allocations: OtpComponentAllocation[] = [
    { kind: 'LongOverlay', targetPct: overlay.value.targetPct, enabled: overlay.value.enabled },
    { kind: 'LongConviction', targetPct: longConviction.value.targetPct, enabled: longConviction.value.enabled },
    { kind: 'ShortConviction', targetPct: shortConviction.value.targetPct, enabled: shortConviction.value.enabled },
  ]
  return {
    ...baseParams,
    useOverlay: overlay.value.enabled,
    overlay: overlay.value.enabled ? {
      triggerThreshold: overlay.value.triggerThreshold,
      overlaySizePct: 0.33,
      spreadBps: overlay.value.spreadBps,
      annualTer: overlay.value.annualTer,
      leverageFactor: overlay.value.leverageFactor,
      dealingChargeGbp: overlay.value.dealingChargeGbp,
      overnightRetMaxPct: gate(overlay.value.overnightGatePct),
      fireWindowEndHourUk: gate(overlay.value.fireWindowEndHourUk),
    } : undefined,
    useLongConviction: longConviction.value.enabled,
    longConviction: longConviction.value.enabled ? {
      triggerThreshold: longConviction.value.triggerThreshold,
      overlaySizePct: 0.33,
      spreadBps: longConviction.value.spreadBps,
      annualTer: longConviction.value.annualTer,
      leverageFactor: longConviction.value.leverageFactor,
      dealingChargeGbp: longConviction.value.dealingChargeGbp,
      overnightRetMaxPct: gate(longConviction.value.overnightGatePct),
      fireWindowEndHourUk: gate(longConviction.value.fireWindowEndHourUk),
    } : undefined,
    useShortConviction: shortConviction.value.enabled,
    shortConviction: shortConviction.value.enabled ? {
      triggerType: 'delta',
      triggerThreshold: shortConviction.value.triggerThreshold,
      hedgeSizeRatio: 1.0,
      spreadBps: shortConviction.value.spreadBps,
      annualTer: shortConviction.value.annualTer,
      leverageFactor: shortConviction.value.leverageFactor,
      bgaBeta: shortConviction.value.bgaBeta,
      cashBufferPct: 0.33,
      dealingChargeGbp: shortConviction.value.dealingChargeGbp,
      overnightRetMinPct: gate(shortConviction.value.overnightGatePct),
      fireWindowEndHourUk: gate(shortConviction.value.fireWindowEndHourUk),
    } : undefined,
    // ALWAYS present — BacktestJobService.SanitizeForAsset throws for
    // assetId 'otp' jobs with a null otpStandalone block.
    otpStandalone: {
      fundingMode: fundingMode.value,
      fixedNotionalGbp: fixedNotionalGbp.value,
      portfolioNavGbp: portfolioNavGbp.value,
      cashReservePct: cashReservePct.value,
      stepAsideSlicePct: stepAsideSlicePct.value,
      allocations,
    },
  }
}

function fmtPct(val: number | null | undefined): string {
  if (val == null) return '--'
  return (val * 100).toFixed(1) + '%'
}
function fmtSharpe(val: number | null | undefined): string {
  if (val == null) return '--'
  return val.toFixed(2)
}
function fmtGbp(val: number | null | undefined): string {
  if (val == null) return '--'
  return '£' + val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const showFunding = ref(true)
const showComponents = ref(true)

const recentJobs = ref<any[]>([])
const activeJobId = ref<string | null>(null)
const activeJob = ref<any>(null)
const loading = ref(false)
const error = ref<string | null>(null)
let pollTimer: ReturnType<typeof setInterval> | null = null

const isJobRunning = computed(() =>
  activeJob.value?.status === 'queued' || activeJob.value?.status === 'running')
const currentResult = computed(() => activeJob.value?.result ?? null)
const runButtonLabel = computed(() => (loading.value ? 'Running…' : 'Run Backtest'))

const resultSpan = computed(() => {
  const r = currentResult.value
  if (!r) return ''
  const sa = activeJob.value?.parameters?.otpStandalone
  const mode = sa?.fundingMode === 'Residual'
    ? `Residual (NAV £${Number(sa.portfolioNavGbp ?? 50000).toLocaleString('en-GB')}, reserve ${((sa.cashReservePct ?? 0.1) * 100).toFixed(0)}%, slice ${((sa.stepAsideSlicePct ?? sa.bgaWeight ?? 0.25) * 100).toFixed(0)}%)`
    : `Fixed notional £${Number(sa?.fixedNotionalGbp ?? 50000).toLocaleString('en-GB')}`
  return `${r.startDate} → ${r.endDate} · ${r.tradingDays ?? ''} days · ${mode}`
})

// ── Preset strip (2026-08-31) ────────────────────────────────────────
// Load + save-as only, mirroring the reduced GGE/Gold precedent minus the
// promote/set-active affordance (this panel never writes live Redis keys).
// A missing store file (fresh env) or a 403 (non-superuser) both resolve
// to an empty list rather than surfacing as a panel error.
const configs = ref<BacktestConfigStore | null>(null)
const selectedPreset = ref('')
const presetNames = computed(() => configs.value ? Object.keys(configs.value.configs) : [])

// ── Shadow-strategy picker (Phase C.4 of the OTP shadow-promotion arc) ──
// Source of truth: backtest_configs/otp.json entries whose params.shadow_strategy
// sub-block is non-null. Six presets shipped at Phase C.2; a seventh
// (cf_nq_shadow_v2) landed 2026-09-06 alongside the v1 notional halving:
//   cf_nq_shadow_v1, cf_nq_shadow_v2, vt_long_m5_shadow_v1,
//   vt_long_m10_shadow_v1, gvs_shadow_v1, oil_cf_shadow_v1, oil_vt_shadow_v1.
// The picker lets the operator pin a preset; every frozen trigger/hold/TER
// field displays read-only (retuning requires a fresh study + new _v2 preset —
// see ShadowStrategyParams.cs' frozen-contract note). Only three cost inputs
// (notional £, spread bps, dealing £/side) are user-editable, matching how
// the NAS100-side backtests treat cost inputs today.
//
// Run submits BacktestParams with the picked preset's params, overriding
// only those three cost inputs on shadowStrategy. Phase C.3.a-d wire the
// dispatch server-side; when C.3.x for a family hasn't shipped yet the
// backend errors and the panel surfaces the message inline via `error`.
const selectedShadowPreset = ref('')
const shadowPresets = computed(() => {
  if (!configs.value) return [] as Array<{ name: string; params: BacktestParams }>
  return Object.entries(configs.value.configs)
    .filter(([, entry]) => entry?.params?.shadowStrategy != null)
    .map(([name, entry]) => ({ name, params: entry.params }))
})
const selectedShadowEntry = computed(() =>
  shadowPresets.value.find(p => p.name === selectedShadowPreset.value) ?? null)
const selectedShadowParams = computed<ShadowStrategyParams | null>(() =>
  selectedShadowEntry.value?.params?.shadowStrategy ?? null)

// User-editable cost overrides. Initialise from the preset when one is
// picked (see watch below); null until then.
const shadowCosts = ref<{ notionalGbp: number; spreadBps: number; dealingChargeGbpPerSide: number } | null>(null)

watch(selectedShadowParams, (sp) => {
  if (sp == null) { shadowCosts.value = null; return }
  shadowCosts.value = {
    notionalGbp: sp.notionalGbp,
    spreadBps: sp.spreadBps,
    dealingChargeGbpPerSide: sp.dealingChargeGbpPerSide,
  }
})

function fmtSignedPct(v: number | null | undefined): string {
  if (v == null) return '—'
  return (v > 0 ? '+' : '') + (v * 100).toFixed(2) + '%'
}
function fmtSharpeAnchor(v: number | null | undefined): string {
  if (v == null) return '—'
  return v.toFixed(3)
}

async function loadConfigs() {
  try {
    configs.value = await api.backtestConfigs('otp')
  } catch {
    configs.value = { configs: {}, active: '' } as BacktestConfigStore
  }
}

function applyComponentLeg(target: Ref<any>, allocation: OtpComponentAllocation | undefined, p: any) {
  const enabled = !!allocation?.enabled && !!p
  target.value = {
    ...target.value,
    enabled,
    targetPct: allocation?.targetPct ?? 0,
    triggerThreshold: p?.triggerThreshold ?? target.value.triggerThreshold,
    spreadBps: p?.spreadBps ?? target.value.spreadBps,
    annualTer: p?.annualTer ?? target.value.annualTer,
    leverageFactor: p?.leverageFactor ?? target.value.leverageFactor,
    dealingChargeGbp: p?.dealingChargeGbp ?? target.value.dealingChargeGbp,
    // Overlay/LongConviction gate on overnightRetMaxPct; ShortConviction
    // (a HedgeParams leg) gates on overnightRetMinPct — only one is ever
    // present on a given leg's params block.
    overnightGatePct: p?.overnightRetMaxPct ?? p?.overnightRetMinPct ?? null,
    fireWindowEndHourUk: p?.fireWindowEndHourUk ?? null,
    ...(p?.bgaBeta !== undefined ? { bgaBeta: p.bgaBeta } : {}),
  }
}

function loadPreset(name: string) {
  if (!name) return
  const p = configs.value?.configs?.[name]?.params as any
  if (!p) return
  const sa = p.otpStandalone ?? {}
  fundingMode.value = sa.fundingMode ?? 'FixedNotional'
  fixedNotionalGbp.value = sa.fixedNotionalGbp ?? 50000
  portfolioNavGbp.value = sa.portfolioNavGbp ?? 50000
  cashReservePct.value = sa.cashReservePct ?? 0.10
  stepAsideSlicePct.value = sa.stepAsideSlicePct ?? sa.bgaWeight ?? 0.25

  const allocations: OtpComponentAllocation[] = sa.allocations ?? []
  const allocFor = (kind: string) => allocations.find((a: any) => a.kind === kind)

  applyComponentLeg(overlay, allocFor('LongOverlay'), p.overlay)
  applyComponentLeg(longConviction, allocFor('LongConviction'), p.longConviction)
  applyComponentLeg(shortConviction, allocFor('ShortConviction'), p.shortConviction)
}

async function saveAsPreset() {
  const name = window.prompt('Save preset as:')
  if (!name) return
  try {
    await api.backtestSaveConfig(name, buildParams(), activeJob.value?.jobId ?? undefined, 'otp')
    await loadConfigs()
    selectedPreset.value = name
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to save preset'
  }
}

async function loadRecentJobs() {
  try {
    const jobs = await api.backtestJobs('otp')
    recentJobs.value = (jobs ?? [])
      .filter((j: any) => j.assetId === 'otp' && j.status === 'completed')
      .sort((a: any, b: any) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime())
      .slice(0, 20)
  } catch { /* silent — recent-jobs list is nice-to-have */ }
}

async function selectRecentJob(jobId: string) {
  if (!jobId) { activeJob.value = null; return }
  stopPolling()
  const cached = recentJobs.value.find((j: any) => j.jobId === jobId)
  if (cached) { activeJob.value = cached; return }
  try { activeJob.value = await api.backtestJob(jobId) }
  catch (e: any) { error.value = e?.message ?? 'failed to load job' }
}

function startPolling(jobId: string) {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const j = await api.backtestJob(jobId)
      activeJob.value = j
      if (j.status === 'completed' || j.status === 'failed') {
        stopPolling()
        loadRecentJobs()
      }
    } catch { /* keep polling */ }
  }, 2000)
}
function stopPolling() { if (pollTimer) { clearInterval(pollTimer); pollTimer = null } }

// Shadow-preset payload: take the preset's whole params (so every non-shadow
// engine flag round-trips exactly as the preset stored it — the shadow
// backtest evaluator reads only shadowStrategy but the engine still ingests
// filters/gates/canonical-signal flags from the preset), then override only
// the three cost inputs on shadowStrategy.
function buildShadowParams(): BacktestParams | null {
  const entry = selectedShadowEntry.value
  const sp = selectedShadowParams.value
  const costs = shadowCosts.value
  if (!entry || !sp || !costs) return null
  return {
    ...entry.params,
    shadowStrategy: {
      ...sp,
      notionalGbp: costs.notionalGbp,
      spreadBps: costs.spreadBps,
      dealingChargeGbpPerSide: costs.dealingChargeGbpPerSide,
    },
  }
}

async function runShadowBacktest() {
  error.value = null
  const params = buildShadowParams()
  if (!params) {
    error.value = 'Pick a shadow strategy first.'
    return
  }
  loading.value = true
  try {
    const res = await api.backtestRun('single', params, { assetId: 'otp' })
    activeJobId.value = res.jobId
    activeJob.value = { jobId: res.jobId, status: 'queued', parameters: params }
    startPolling(res.jobId)
  } catch (e: any) {
    // C.3.a-d land per-family; while a family's evaluator isn't wired,
    // the backend returns a descriptive error — surface it verbatim so
    // the operator knows which preset needs its C.3.x PR to land.
    error.value = e?.message ?? 'shadow run failed'
  } finally {
    loading.value = false
  }
}

async function runBacktest() {
  error.value = null
  if (decommissionError.value) {
    error.value = decommissionError.value
    return
  }
  if (allocationError.value) {
    error.value = allocationError.value
    return
  }
  loading.value = true
  try {
    const res = await api.backtestRun('single', buildParams(), { assetId: 'otp' })
    activeJobId.value = res.jobId
    activeJob.value = { jobId: res.jobId, status: 'queued' }
    startPolling(res.jobId)
  } catch (e: any) {
    error.value = e?.message ?? 'run failed'
  } finally {
    loading.value = false
  }
}

onMounted(() => { loadConfigs(); loadRecentJobs() })
onUnmounted(() => stopPolling())
</script>

<template>
  <div class="otp-backtest" data-testid="otp-backtest-panel">
    <h2>OTP Backtest — Standalone</h2>

    <div class="decomm-banner" data-testid="otp-decomm-banner" role="note">
      <strong>LongConviction / LongOverlay / ShortConviction were decommissioned 2026-09-06.</strong>
      See
      <code>docs/superpowers/plans/2026-09-06-otp-shadow-promotion-arc.md</code>.
      Use the shadow-strategy presets in Phase C.4. Existing presets remain
      inspectable in the read-only section below; new backtests cannot enable
      these three components.
    </div>

    <div v-if="error" class="error" data-testid="run-error">{{ error }}</div>

    <div class="section shadow-picker-section" data-testid="otp-shadow-picker">
      <h3 class="section-toggle">Shadow Strategy Picker <span class="pill">Phase C.4</span></h3>
      <p class="section-hint">
        Pick one of the seven frozen shadow-strategy presets from
        <code>backtest_configs/otp.json</code>. Every trigger / hold / TER
        parameter is read-only — retuning requires a fresh study and a new
        <code>_vN</code> preset (e.g. <code>cf_nq_shadow_v2</code>, added
        2026-09-06 as the pre-registered follow-up to <code>cf_nq_shadow_v1</code>).
        Only the three cost inputs (notional, spread, dealing charge) are
        editable.
      </p>
      <label class="shadow-picker-label">Strategy:
        <select v-model="selectedShadowPreset" data-testid="otp-shadow-preset-select">
          <option value="">— select —</option>
          <option v-for="p in shadowPresets" :key="p.name" :value="p.name" :data-testid="`otp-shadow-option-${p.name}`">
            {{ p.name }}
            (Sharpe anchor {{ fmtSharpeAnchor(p.params.shadowStrategy?.studyExpectedSharpe) }})
          </option>
        </select>
      </label>

      <div v-if="selectedShadowParams && shadowCosts" class="shadow-detail" data-testid="otp-shadow-detail">
        <div class="shadow-detail-grid">
          <div class="frozen-block" data-testid="otp-shadow-frozen">
            <div class="frozen-label">FROZEN (read-only)</div>
            <dl class="frozen-dl">
              <dt>Signal family</dt>
              <dd data-testid="otp-shadow-frozen-signal-family">{{ selectedShadowParams.signalFamily }}</dd>
              <dt>Underlying instrument</dt>
              <dd data-testid="otp-shadow-frozen-underlying">{{ selectedShadowParams.underlyingInstrument }}</dd>
              <dt>Lookback (LSE sessions)</dt>
              <dd data-testid="otp-shadow-frozen-lookback">{{ selectedShadowParams.lookbackDays ?? '—' }}</dd>
              <dt>Trigger threshold</dt>
              <dd data-testid="otp-shadow-frozen-threshold">{{ fmtSignedPct(selectedShadowParams.threshold) }}</dd>
              <dt>Overnight threshold</dt>
              <dd data-testid="otp-shadow-frozen-overnight">{{ fmtSignedPct(selectedShadowParams.overnightThreshold) }}</dd>
              <dt>Hold (LSE sessions)</dt>
              <dd data-testid="otp-shadow-frozen-hold">{{ selectedShadowParams.holdDays }}</dd>
              <dt>Leverage</dt>
              <dd data-testid="otp-shadow-frozen-leverage">{{ selectedShadowParams.leverageFactor }}×</dd>
              <dt>Annual TER</dt>
              <dd data-testid="otp-shadow-frozen-ter">{{ (selectedShadowParams.annualTer * 100).toFixed(2) }}%</dd>
              <dt>Study Sharpe anchor</dt>
              <dd data-testid="otp-shadow-frozen-sharpe">{{ fmtSharpeAnchor(selectedShadowParams.studyExpectedSharpe) }}</dd>
              <dt>Source</dt>
              <dd class="source-doc" data-testid="otp-shadow-frozen-source"><code>{{ selectedShadowParams.sourceDoc }}</code></dd>
            </dl>
          </div>
          <div class="editable-block" data-testid="otp-shadow-editable">
            <div class="editable-label">EDITABLE cost inputs</div>
            <label>Notional per fire (£):
              <input type="number" min="1" step="1000" v-model.number="shadowCosts.notionalGbp"
                     data-testid="otp-shadow-notional" />
            </label>
            <label>Spread (bps, round-trip):
              <input type="number" min="0" step="0.5" v-model.number="shadowCosts.spreadBps"
                     data-testid="otp-shadow-spread-bps" />
            </label>
            <label>Dealing charge (£/side):
              <input type="number" min="0" step="0.05" v-model.number="shadowCosts.dealingChargeGbpPerSide"
                     data-testid="otp-shadow-dealing-charge" />
            </label>
          </div>
        </div>
        <button class="btn-run primary-run-btn shadow-run-btn"
                data-testid="otp-shadow-run-button"
                :disabled="loading || isJobRunning"
                @click="runShadowBacktest">
          {{ loading ? 'Running…' : 'Run Shadow Backtest' }}
        </button>
      </div>
      <p v-else class="section-hint" data-testid="otp-shadow-empty">
        No shadow preset picked yet. Seven frozen presets are shipped in
        <code>backtest_configs/otp.json</code>: cf_nq_shadow_v1,
        cf_nq_shadow_v2, vt_long_m5_shadow_v1, vt_long_m10_shadow_v1,
        gvs_shadow_v1, oil_cf_shadow_v1, oil_vt_shadow_v1.
      </p>
    </div>

    <div class="section preset-section">
      <label>Preset:
        <select v-model="selectedPreset" @change="loadPreset(selectedPreset)" data-testid="otp-preset-select">
          <option value="">— select —</option>
          <option v-for="n in presetNames" :key="n" :value="n">
            {{ n }}{{ n === configs?.active ? ' (active)' : '' }}
          </option>
        </select>
      </label>
      <button class="btn-sm" data-testid="otp-preset-save" @click="saveAsPreset">Save as…</button>
    </div>

    <div class="section">
      <h3 class="section-toggle" @click="showFunding = !showFunding">
        Funding <span class="toggle-icon">{{ showFunding ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showFunding" class="section-body">
        <label class="checkbox-label">
          <input type="radio" value="FixedNotional" v-model="fundingMode" data-testid="otp-funding-fixed" /> Fixed notional (£)
        </label>
        <label class="checkbox-label">
          <input type="radio" value="Residual" v-model="fundingMode" data-testid="otp-funding-residual" /> Residual (carousel)
        </label>
        <template v-if="fundingMode === 'FixedNotional'">
          <p class="section-hint">Every fire is sized from the same pool — no compounding. £50k ≈ the idle carousel cash on a deep-drop day and the live LQQ3 ticket size.</p>
          <label>Pool per day (£):
            <input type="number" step="1000" min="1" v-model.number="fixedNotionalGbp" data-testid="otp-fixed-notional-input" />
          </label>
        </template>
        <template v-else>
          <p class="section-hint">Pool = NAV × (reserve + step-aside slice on NQ ≤ 0 days), compounding off the run's own NPV. No interest accrues on the pool.</p>
          <label>Portfolio NAV seed (£):
            <input type="number" step="1000" min="1" v-model.number="portfolioNavGbp" data-testid="otp-portfolio-nav-input" />
          </label>
          <label>Cash reserve (% of NAV):
            <input type="number" step="0.01" min="0" max="0.99" v-model.number="cashReservePct" data-testid="otp-cash-reserve-input" />
          </label>
          <label>Step-aside slice (0.25 = BGA, 0.50 = BGA + JH):
            <input type="number" step="0.01" min="0" max="1" v-model.number="stepAsideSlicePct" data-testid="otp-step-aside-slice-input" />
          </label>
        </template>
      </div>
    </div>

    <div class="section">
      <h3 class="section-toggle" @click="showComponents = !showComponents">
        Components <span class="toggle-icon">{{ showComponents ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showComponents">
        <div v-if="allocationError" class="error" data-testid="otp-alloc-error">{{ allocationError }}</div>
        <div v-if="decommissionError" class="error" data-testid="otp-decomm-error">{{ decommissionError }}</div>

        <details class="decomm-collapsible" data-testid="otp-decomm-collapsible">
          <summary>Show decommissioned strategies (read-only)</summary>
          <p class="section-hint">
            Long Conviction / Long Overlay / Short Conviction were decommissioned
            on 2026-09-06 (Phase B). The toggles and knobs below are disabled;
            historical preset payloads are still parsed for replay via the
            preset dropdown above, but no new run can enable them. Use the
            shadow-strategy presets in Phase C.4 for new work.
          </p>

        <fieldset class="component-block" data-testid="otp-component-longOverlay" disabled>
          <label class="checkbox-label">
            <input type="checkbox" v-model="overlay.enabled" data-testid="otp-enabled-longOverlay" disabled /> Long Overlay
          </label>
          <template v-if="overlay.enabled">
            <label>Target % of pool:
              <input type="number" step="0.01" min="0" max="1" v-model.number="overlay.targetPct" data-testid="otp-target-longOverlay" disabled />
            </label>
            <label>Trigger threshold:
              <input type="number" step="0.0001" v-model.number="overlay.triggerThreshold" data-testid="otp-trigger-longOverlay" disabled />
            </label>
            <label>Spread (bps):
              <input type="number" step="0.0001" v-model.number="overlay.spreadBps" data-testid="otp-spread-longOverlay" disabled />
            </label>
            <label>Annual TER:
              <input type="number" step="0.0001" v-model.number="overlay.annualTer" data-testid="otp-ter-longOverlay" disabled />
            </label>
            <label>Leverage:
              <input type="number" step="0.01" v-model.number="overlay.leverageFactor" data-testid="otp-leverage-longOverlay" disabled />
            </label>
            <label>Dealing charge (£):
              <input type="number" step="0.01" v-model.number="overlay.dealingChargeGbp" data-testid="otp-dealing-longOverlay" disabled />
            </label>
            <label>Overnight gate — fire only if NQ 08:40 ≤ (%):
              <input type="number" step="0.05" placeholder="off" v-model.number="overlay.overnightGatePct" data-testid="otp-overnight-longOverlay" disabled />
            </label>
            <label>No fires at/after (UK hour):
              <input type="number" step="1" min="0" max="23" placeholder="off" v-model.number="overlay.fireWindowEndHourUk" data-testid="otp-firewindow-longOverlay" disabled />
            </label>
          </template>
        </fieldset>

        <fieldset class="component-block" data-testid="otp-component-longConviction" disabled>
          <label class="checkbox-label">
            <input type="checkbox" v-model="longConviction.enabled" data-testid="otp-enabled-longConviction" /> Long Conviction
          </label>
          <template v-if="longConviction.enabled">
            <label>Target % of pool:
              <input type="number" step="0.01" min="0" max="1" v-model.number="longConviction.targetPct" data-testid="otp-target-longConviction" />
            </label>
            <label>Trigger threshold:
              <input type="number" step="0.0001" v-model.number="longConviction.triggerThreshold" data-testid="otp-trigger-longConviction" />
            </label>
            <label>Spread (bps):
              <input type="number" step="0.0001" v-model.number="longConviction.spreadBps" data-testid="otp-spread-longConviction" />
            </label>
            <label>Annual TER:
              <input type="number" step="0.0001" v-model.number="longConviction.annualTer" data-testid="otp-ter-longConviction" />
            </label>
            <label>Leverage:
              <input type="number" step="0.01" v-model.number="longConviction.leverageFactor" data-testid="otp-leverage-longConviction" />
            </label>
            <label>Dealing charge (£):
              <input type="number" step="0.01" v-model.number="longConviction.dealingChargeGbp" data-testid="otp-dealing-longConviction" />
            </label>
            <label>Overnight gate — fire only if NQ 08:40 ≤ (%):
              <input type="number" step="0.05" placeholder="off" v-model.number="longConviction.overnightGatePct" data-testid="otp-overnight-longConviction" />
            </label>
            <label>No fires at/after (UK hour):
              <input type="number" step="1" min="0" max="23" placeholder="off" v-model.number="longConviction.fireWindowEndHourUk" data-testid="otp-firewindow-longConviction" />
            </label>
          </template>
        </fieldset>

        <fieldset class="component-block" data-testid="otp-component-shortConviction" disabled>
          <label class="checkbox-label">
            <input type="checkbox" v-model="shortConviction.enabled" data-testid="otp-enabled-shortConviction" /> Short Conviction
          </label>
          <template v-if="shortConviction.enabled">
            <label>Target % of pool:
              <input type="number" step="0.01" min="0" max="1" v-model.number="shortConviction.targetPct" data-testid="otp-target-shortConviction" />
            </label>
            <label>Trigger threshold:
              <input type="number" step="0.0001" v-model.number="shortConviction.triggerThreshold" data-testid="otp-trigger-shortConviction" />
            </label>
            <label>Spread (bps):
              <input type="number" step="0.0001" v-model.number="shortConviction.spreadBps" data-testid="otp-spread-shortConviction" />
            </label>
            <label>Annual TER:
              <input type="number" step="0.0001" v-model.number="shortConviction.annualTer" data-testid="otp-ter-shortConviction" />
            </label>
            <label>Leverage:
              <input type="number" step="0.01" v-model.number="shortConviction.leverageFactor" data-testid="otp-leverage-shortConviction" />
            </label>
            <label>BGA beta:
              <input type="number" step="0.0001" v-model.number="shortConviction.bgaBeta" data-testid="otp-bgabeta-shortConviction" />
            </label>
            <label>Dealing charge (£):
              <input type="number" step="0.01" v-model.number="shortConviction.dealingChargeGbp" data-testid="otp-dealing-shortConviction" />
            </label>
            <label>Overnight gate — fire only if NQ 08:40 ≥ (%):
              <input type="number" step="0.05" placeholder="off" v-model.number="shortConviction.overnightGatePct" data-testid="otp-overnight-shortConviction" />
            </label>
            <label>No fires at/after (UK hour):
              <input type="number" step="1" min="0" max="23" placeholder="off" v-model.number="shortConviction.fireWindowEndHourUk" data-testid="otp-firewindow-shortConviction" />
            </label>
          </template>
        </fieldset>

        </details>

        <div class="run-preview" data-testid="otp-allocation-preview">
          Σ allocated: {{ (allocationSum * 100).toFixed(1) }}%
        </div>
      </div>
    </div>

    <div class="section actions-section">
      <button class="btn-run primary-run-btn" data-testid="run-button" @click="runBacktest"
              :disabled="loading || isJobRunning || !!allocationError || !!decommissionError">
        {{ runButtonLabel }}
      </button>
      <p v-if="activeJobId" class="job-id">Active job: {{ activeJobId }}</p>
    </div>

    <div class="section results-section" data-testid="results-column">
      <div class="results-header">
        <label>Load previous run:
          <select data-testid="recent-jobs-select"
                  @change="selectRecentJob(($event.target as HTMLSelectElement).value)">
            <option value="">— select —</option>
            <option v-for="j in recentJobs" :key="j.jobId" :value="j.jobId">
              {{ j.completedAt?.slice(0, 16).replace('T', ' ') }} · Sharpe {{ j.result?.sharpeRatio?.toFixed(2) ?? '—' }}
            </option>
          </select>
        </label>
      </div>

      <div v-if="!activeJob && !loading" data-testid="no-results" class="no-results">
        Run a backtest to see results, or load a previous run above.
      </div>
      <div v-else-if="loading" data-testid="job-loading" class="loading">Submitting…</div>

      <div v-if="activeJob && (activeJob.status === 'queued' || activeJob.status === 'running')"
           data-testid="job-progress" class="job-progress">
        {{ activeJob.status }}…
      </div>

      <div v-if="currentResult" data-testid="strategy-banner" class="strategy-banner">
        {{ currentResult.strategyName }}
      </div>

      <div v-if="currentResult" class="result-span" data-testid="otp-result-span">{{ resultSpan }}</div>

      <div v-if="currentResult" class="kpi-cards" data-testid="otp-result-kpi-cards">
        <div class="kpi-card">
          <div class="kpi-label">CAGR</div>
          <div class="kpi-value" data-testid="kpi-cagr">{{ fmtPct(currentResult.cagrPct) }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Sharpe</div>
          <div class="kpi-value" data-testid="kpi-sharpe">{{ fmtSharpe(currentResult.sharpeRatio) }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Max DD</div>
          <div class="kpi-value" data-testid="kpi-maxdd">{{ fmtPct(currentResult.maxDrawdownPct) }}</div>
        </div>
      </div>

      <div v-if="currentResult" class="component-summary" data-testid="otp-summary-longOverlay">
        <div class="section-label">LONG OVERLAY SUMMARY</div>
        <div class="kpi-grid">
          <div class="kpi"><div class="kpi-label">Days</div><div class="kpi-value">{{ currentResult.overlayDaysCount }}</div></div>
          <div class="kpi"><div class="kpi-label">Win Rate</div><div class="kpi-value">{{ (currentResult.overlayWinRate * 100).toFixed(0) }}%</div></div>
          <div class="kpi"><div class="kpi-label">Total P&amp;L</div><div class="kpi-value">{{ fmtGbp(currentResult.overlayTotalPnl) }}</div></div>
          <div class="kpi"><div class="kpi-label">Total Costs</div><div class="kpi-value">{{ fmtGbp(currentResult.overlayTotalCosts) }}</div></div>
        </div>
      </div>

      <div v-if="currentResult" class="component-summary" data-testid="otp-summary-longConviction">
        <div class="section-label">LONG CONVICTION SUMMARY</div>
        <div class="kpi-grid">
          <div class="kpi"><div class="kpi-label">Days</div><div class="kpi-value">{{ currentResult.longConvictionDaysCount }}</div></div>
          <div class="kpi"><div class="kpi-label">Win Rate</div><div class="kpi-value">{{ (currentResult.longConvictionWinRate * 100).toFixed(0) }}%</div></div>
          <div class="kpi"><div class="kpi-label">Total P&amp;L</div><div class="kpi-value">{{ fmtGbp(currentResult.longConvictionTotalPnl) }}</div></div>
          <div class="kpi"><div class="kpi-label">Total Costs</div><div class="kpi-value">{{ fmtGbp(currentResult.longConvictionTotalCosts) }}</div></div>
        </div>
      </div>

      <div v-if="currentResult" class="component-summary" data-testid="otp-summary-shortConviction">
        <div class="section-label">SHORT CONVICTION SUMMARY</div>
        <div class="kpi-grid">
          <div class="kpi"><div class="kpi-label">Days</div><div class="kpi-value">{{ currentResult.shortConvictionDaysCount }}</div></div>
          <div class="kpi"><div class="kpi-label">Win Rate</div><div class="kpi-value">{{ (currentResult.shortConvictionWinRate * 100).toFixed(0) }}%</div></div>
          <div class="kpi"><div class="kpi-label">Total P&amp;L</div><div class="kpi-value">{{ fmtGbp(currentResult.shortConvictionTotalPnl) }}</div></div>
          <div class="kpi"><div class="kpi-label">Total Costs</div><div class="kpi-value">{{ fmtGbp(currentResult.shortConvictionTotalCosts) }}</div></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.otp-backtest { padding: 1rem; color: #ccc; }
.otp-backtest h2 { color: #eee; margin: 0 0 1rem; font-size: 1rem; }
.error { color: #f87171; padding: 0.5rem; margin-bottom: 0.75rem; background: #2a1010; border: 1px solid #7f1d1d; border-radius: 4px; }
.decomm-banner {
  background: #2a2210; border: 1px solid #6b5a1f; border-radius: 4px;
  padding: 0.6rem 0.75rem; margin-bottom: 0.75rem;
  font-size: 0.8rem; color: #e0c86b; line-height: 1.45;
}
.decomm-banner code { background: #1a1a1a; padding: 1px 4px; border-radius: 3px; font-size: 0.75rem; color: #eec; }
.decomm-collapsible {
  background: #14181c; border: 1px solid #2a2f36; border-radius: 4px;
  padding: 0.4rem 0.6rem; margin: 0.6rem 0;
}
.decomm-collapsible > summary {
  cursor: pointer; user-select: none; font-size: 0.8rem;
  color: #b8a768; letter-spacing: 0.5px; padding: 0.15rem 0;
}
.decomm-collapsible[open] > summary { border-bottom: 1px solid #333; margin-bottom: 0.5rem; padding-bottom: 0.35rem; }
.decomm-collapsible fieldset.component-block { border: 1px dashed #3a3a3a; opacity: 0.7; }
.decomm-collapsible fieldset.component-block[disabled] { pointer-events: none; }

.section { margin-bottom: 1rem; padding: 0.75rem; background: #1a1a1a; border: 1px solid #333; border-radius: 4px; }
.preset-section { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.btn-sm { background: #333; border: 1px solid #444; color: #ccc; padding: 4px 10px; border-radius: 4px; font-size: 11px; cursor: pointer; }
.btn-sm:hover { background: #444; }
.section-toggle {
  cursor: pointer; user-select: none; font-size: 0.9rem; color: #aaa;
  border-bottom: 1px solid #333; padding-bottom: 0.3rem; margin: 0;
  text-transform: uppercase; letter-spacing: 1px;
}
.section-toggle:hover { color: #e0e0e0; }
.toggle-icon { font-size: 0.8rem; margin-left: 0.5rem; }
.section-body { margin-top: 0.5rem; }
.section-hint { font-size: 0.72rem; color: #776; line-height: 1.4; margin: 0.25rem 0 0.6rem; }

label { display: block; margin: 0.35rem 0; font-size: 0.85rem; }
input[type="number"] { background: #111; color: #ccc; border: 1px solid #444; border-radius: 3px; padding: 3px 6px; margin-left: 6px; width: 110px; }
.checkbox-label { display: flex; align-items: center; gap: 6px; font-weight: 600; color: #ddd; }

.component-block { padding: 0.6rem; margin-top: 0.6rem; background: #14181c; border: 1px solid #2a2f36; border-radius: 4px; }
fieldset.component-block { min-inline-size: 0; }
fieldset.component-block > label { min-inline-size: 0; }
.run-preview { font-size: 0.75rem; color: #888; margin-top: 0.5rem; font-family: monospace; }

.btn-run { display: block; width: 100%; padding: 0.6rem 1rem; border-radius: 4px; border: none; cursor: pointer; font-weight: 600; font-size: 0.9rem; }
.btn-run:disabled { opacity: 0.6; cursor: default; }
.primary-run-btn { background: #2e7d32; color: #fff; }
.primary-run-btn:hover:not(:disabled) { background: #388e3c; }
.job-id { color: #888; font-family: monospace; font-size: 0.85rem; margin-top: 0.5rem; }

.results-header { margin-bottom: 0.75rem; }
.no-results, .loading {
  display: flex; align-items: center; justify-content: center; min-height: 120px;
  color: #555; font-style: italic; font-size: 0.85rem; text-align: center;
}
.job-progress { margin-bottom: 1rem; padding: 0.6rem; background: #1a2a1a; border: 1px solid #2a4a2a; border-radius: 4px; font-size: 0.8rem; color: #4caf50; }

.strategy-banner {
  background: linear-gradient(135deg, #1a2a1a, #0a0a0a);
  border: 1px solid #2a4a2a; border-radius: 4px; padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem; font-size: 0.85rem; font-weight: 600; color: #4caf50;
  text-align: center; letter-spacing: 0.5px;
}
.result-span { font-size: 0.8rem; color: #9aa; margin: 0.25rem 0 0.5rem; }

.kpi-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 0.75rem; margin-bottom: 0.75rem; }
.kpi-card { background: #1a1a1a; border: 1px solid #333; border-radius: 4px; padding: 0.75rem; text-align: center; }
.kpi-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-bottom: 0.25rem; }
.kpi-value { font-size: 1.1rem; color: #eee; font-weight: 600; }

.component-summary { margin-top: 0.75rem; }
.section-label { font-size: 10px; color: #666; text-transform: uppercase; margin: 10px 0 6px; letter-spacing: 0.5px; }
.kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(90px, 1fr)); gap: 0.5rem; }
.kpi { background: #1a1a1a; border: 1px solid #333; border-radius: 4px; padding: 0.5rem; text-align: center; }
.kpi .kpi-value { font-size: 0.95rem; }

@media (max-width: 600px) {
  .kpi-cards, .kpi-grid { grid-template-columns: repeat(2, 1fr); }
}

/* Shadow-strategy picker (Phase C.4) */
.shadow-picker-section {
  border-color: #2a4a6a; background: #101820;
}
.shadow-picker-section .section-toggle { color: #7fb0e0; cursor: default; border-bottom-color: #2a4a6a; }
.shadow-picker-section .pill {
  background: #1a3a5a; color: #a0c8e0; font-size: 0.6rem; padding: 2px 6px;
  border-radius: 8px; margin-left: 0.5rem; letter-spacing: 0.5px; font-weight: 600;
}
.shadow-picker-label { display: block; margin: 0.5rem 0 0.75rem; font-weight: 600; color: #ddd; }
.shadow-picker-label select { background: #111; color: #ccc; border: 1px solid #444; border-radius: 3px; padding: 3px 6px; margin-left: 8px; min-width: 260px; }
.shadow-detail { margin-top: 0.5rem; }
.shadow-detail-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem;
}
.frozen-block, .editable-block {
  background: #14181c; border: 1px solid #2a2f36; border-radius: 4px; padding: 0.6rem 0.75rem;
}
.frozen-block { border-left: 3px solid #4a5f8a; }
.editable-block { border-left: 3px solid #4a8a5a; }
.frozen-label, .editable-label {
  font-size: 0.65rem; letter-spacing: 1px; text-transform: uppercase;
  color: #778; margin-bottom: 0.5rem;
}
.frozen-block .frozen-label { color: #7f97c0; }
.editable-block .editable-label { color: #7fc097; }
.frozen-dl {
  display: grid; grid-template-columns: max-content 1fr; gap: 0.2rem 0.75rem;
  margin: 0; font-size: 0.78rem;
}
.frozen-dl dt { color: #888; }
.frozen-dl dd { color: #ddd; margin: 0; font-family: monospace; }
.frozen-dl dd.source-doc code { font-size: 0.7rem; color: #b8c8d8; background: transparent; padding: 0; }
.shadow-run-btn { margin-top: 0.75rem; background: #2e5d7d; }
.shadow-run-btn:hover:not(:disabled) { background: #3a7099; }
@media (max-width: 600px) {
  .shadow-detail-grid { grid-template-columns: 1fr; }
}
</style>
